import React from 'react';
import ServiceLandingPage from './ServiceLandingPage';
import { useDocumentSEO } from '../utils/useDocumentSEO';

/**
 * /gestao-presencial
 * Positioning: Marketing terceirizado — a Coutin funciona como uma equipe
 * de marketing completa dentro da empresa do cliente (squad terceirizado).
 * Curto, direto, punchy.
 */
const GestaoPresencialPage = () => {
  useDocumentSEO({
    title:
      'Marketing Terceirizado para Empresas | Squad Completo de Marketing — Coutin Agência',
    description:
      'Terceirize o marketing da sua empresa com um squad completo: planejamento, conteúdo, audiovisual, redes sociais, sites e campanhas sob uma única operação. Atendimento presencial em Foz do Iguaçu e híbrido para todo o Brasil.',
    canonical: 'https://www.agenciacoutin.com.br/gestao-presencial',
    ogImage:
      'https://images.unsplash.com/photo-1552664730-d307ca884978?q=85&w=1200&auto=format&fit=crop',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Marketing Terceirizado / Gestão de Marketing',
      provider: {
        '@type': 'Organization',
        name: 'Coutin Agência',
        url: 'https://www.agenciacoutin.com.br',
        telephone: '+55-45-98806-8508',
      },
      areaServed: [
        { '@type': 'City', name: 'Foz do Iguaçu' },
        { '@type': 'Country', name: 'Brasil' },
      ],
      description:
        'Operação completa de marketing como extensão da equipe do cliente: estratégia, conteúdo, audiovisual, redes sociais, design, sites e campanhas.',
      url: 'https://www.agenciacoutin.com.br/gestao-presencial',
    },
  });

  return (
    <ServiceLandingPage
      gaSource="page-gestao-presencial"
      showLocalProof={true}
      overline="Marketing Terceirizado"
      h1="Sua empresa não precisa contratar cinco fornecedores. Precisa de uma operação."
      highlightWord="uma operação"
      subtitle="A Coutin funciona como uma extensão do seu marketing. Um squad completo — estratégia, conteúdo, audiovisual, design, redes sociais, sites e campanhas — sob uma mesma marca, com o mesmo time cuidando do começo ao fim."
      whatsappMessage="Olá! Vim pela página de Marketing Terceirizado e quero conversar sobre montar um squad de marketing para minha empresa. Ramo/porte:"
      heroImage="https://images.unsplash.com/photo-1552664730-d307ca884978?q=85&w=1200&auto=format&fit=crop"
      benefits={[
        { value: '1', label: 'Operação Única' },
        { value: 'Squad', label: 'Time Multidisciplinar' },
        { value: 'Mensal', label: 'Contrato Recorrente' },
        { value: 'Foz', label: 'Presencial + Híbrido' },
      ]}
      intro={[
        'A maioria das empresas hoje contrata um social media, um designer freelancer, um videomaker avulso, uma agência de tráfego e um desenvolvedor de site. Cinco fornecedores, cinco contratos, cinco linguagens diferentes e ninguém, de fato, dono do resultado.',
        'A gestão de marketing terceirizada da Coutin resolve isso. Você contrata uma operação — não peças soltas. Um time multidisciplinar assume o marketing da sua empresa de ponta a ponta: da estratégia à execução, do conteúdo diário à campanha grande, do site institucional ao vídeo de campanha.',
        'É o modelo ideal para empresas que já entenderam que marketing precisa ter dono, ritmo e continuidade — mas que ainda não têm (ou não querem manter) um departamento interno completo.',
      ]}
      blocks={[
        {
          h2: 'O que entra no squad',
          subtitle:
            'Uma equipe completa sob uma mesma operação. Nada de terceirizar em cinco cantos diferentes.',
          list: [
            'Planejamento estratégico e calendário editorial mensal',
            'Gestão profissional de redes sociais (Instagram, Facebook, TikTok, LinkedIn)',
            'Produção audiovisual (filmagem, drones, edição, motion)',
            'Fotografia profissional para catálogo, mídia e institucional',
            'Design gráfico, identidade visual e materiais de campanha',
            'Criação e manutenção de sites, landing pages e blog',
            'Campanhas de tráfego pago (Google Ads e Meta Ads)',
            'Relatórios mensais claros — o que foi feito, o que gerou resultado',
          ],
        },
        {
          h2: 'Como funciona na prática',
          list: [
            'Reunião de imersão para entender negócio, produto, público e objetivos',
            'Planejamento estratégico de 90 dias com metas claras',
            'Squad dedicado com ponto focal único (você não fala com cinco fornecedores)',
            'Rotina mensal de produção, publicação, campanhas e mensuração',
            'Reuniões periódicas de alinhamento — presenciais em Foz ou online',
            'Contrato mensal recorrente, com escopo transparente',
          ],
        },
        {
          h2: 'Para quem faz sentido',
          subtitle:
            'A gestão terceirizada não é para todo mundo. É para empresas que já entenderam que marketing exige método.',
          list: [
            'Empresas que já têm produto/serviço validado e querem escalar presença',
            'Negócios que perderam tempo (e dinheiro) com fornecedores desconectados',
            'Empresários que não querem montar um departamento de marketing interno',
            'Marcas que precisam de constância — não campanhas soltas',
            'Empresas em Foz do Iguaçu, Ciudad del Este, Puerto Iguazú e região',
            'Negócios em qualquer região do Brasil dispostos ao modelo híbrido',
          ],
        },
        {
          h2: 'Por que a Coutin, e não uma agência qualquer',
          paragraphs: [
            'A Coutin não é uma agência genérica. É uma operação híbrida: produção + estratégia + execução sob a mesma marca. O mesmo time que planeja é o que produz, o que publica, o que mensura. Isso elimina os ruídos típicos entre fornecedores diferentes — e é por isso que os resultados aparecem.',
            'Trabalhamos com foco em confidencialidade quando o cliente precisa, em white-label quando o parceiro precisa, e em atendimento direto sempre. Não inventamos cases, não inflamos números, não prometemos milagre. Entregamos operação séria, com ritmo e responsabilidade.',
          ],
        },
      ]}
    />
  );
};

export default GestaoPresencialPage;
